/**
 * Coverage — an industry-style read of the writer's draft.
 *
 * Generates a professional coverage report (logline, synopsis, six-category
 * scores with written reasoning, strengths, weaknesses, and a Pass / Consider
 * / Recommend verdict) and keeps a history of past reads.
 *
 * Two shells: a modal (mobile) and an `embedded` mode used inside the right
 * Tools sidebar on desktop.
 *
 * Brand: never says "AI" — it's "Coverage", a reader's report.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  X, Loader2, FileText, RefreshCw, Check, AlertTriangle, Clock, Trash2, ScrollText, Highlighter, Compass,
} from "lucide-react";
import { MarkdownLite } from "@/components/MarkdownLite";

const VERDICT_STYLE = {
  Recommend: { bg: "var(--ok)", label: "Recommend" },
  Consider: { bg: "var(--warn)", label: "Consider" },
  Pass: { bg: "var(--err)", label: "Pass" },
};

function scoreColor(s) {
  if (s >= 8) return "var(--ok)";
  if (s >= 6) return "var(--warn)";
  return "var(--err)";
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
}

export function CoveragePanel({ scriptId, open, onClose, annotationsOn = false, onToggleAnnotations, onAnnotations, embedded = false, scriptGenre = "", onBeforeRun, editSignal, onCoachFocus }) {
  const confirm = useConfirm();
  const [reports, setReports] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const pollRef = useRef(null);
  const chainedRef = useRef(new Set());
  const annotationsOnRef = useRef(annotationsOn);
  annotationsOnRef.current = annotationsOn;

  const selected = reports.find((r) => r.id === selectedId) || null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/scripts/${scriptId}/coverage`);
      const list = data.reports || [];
      setReports(list);
      setSelectedId((cur) => cur || (list[0]?.id ?? null));
      // resume polling if the newest is still generating
      if (list[0]?.status === "generating" || list[0]?.annotations_status === "generating") startPolling(list[0].id);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [scriptId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open || embedded) load();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, embedded, load]);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  // The editor just saved — re-check whether the selected read still matches the draft.
  useEffect(() => {
    if (!editSignal || !selectedId) return;
    const r = reports.find((x) => x.id === selectedId);
    if (!r || r.status !== "ready" || pollRef.current) return;
    api.get(`/scripts/${scriptId}/coverage/${selectedId}`)
      .then(({ data }) => setReports((prev) => prev.map((x) => (x.id === data.report.id ? data.report : x))))
      .catch(() => {});
  }, [editSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  const startPolling = useCallback((reportId) => {
    stopPolling();
    setGenerating(true);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/scripts/${scriptId}/coverage/${reportId}`);
        const r = data.report;
        setReports((prev) => prev.map((x) => (x.id === r.id ? r : x)));
        const busy = r.status === "generating" || r.annotations_status === "generating";
        if (!busy) {
          if (r.status === "ready" && annotationsOnRef.current &&
              (!r.annotations_status || r.annotations_status === "none") && !chainedRef.current.has(r.id)) {
            // The user wants margin notes but this read was made without them — chain a markup pass.
            chainedRef.current.add(r.id);
            api.post(`/scripts/${scriptId}/coverage/${r.id}/annotations`)
              .then(({ data: d }) => setReports((prev) => prev.map((x) => (x.id === d.report.id ? d.report : x))))
              .catch(() => {});
            return;
          }
          stopPolling();
          setGenerating(false);
          if (r.status === "error") toast.error(r.error || "Coverage failed. Please try again.");
          else if (r.annotations_status === "error") toast.error("Couldn't mark up the script. Flip the toggle to try again.");
          if (annotationsOnRef.current && r.annotations_status === "ready") onAnnotations?.(r.annotations || [], r.id);
        }
      } catch { /* keep polling */ }
    }, 3000);
  }, [scriptId]);

  const runCoverage = async () => {
    setGenerating(true);
    try {
      await onBeforeRun?.(); // flush any pending editor autosave so the reader gets the exact current text
      const { data } = await api.post(`/scripts/${scriptId}/coverage${annotationsOn ? "?annotate=true" : ""}`);
      const r = data.report;
      setReports((prev) => [r, ...prev]);
      setSelectedId(r.id);
      startPolling(r.id);
    } catch (e) {
      setGenerating(false);
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start the read.");
    }
  };

  const deleteReport = async (id) => {
    const ok = await confirm({ title: "Delete this coverage?", description: "This read will be removed for good.", confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    try {
      await api.delete(`/scripts/${scriptId}/coverage/${id}`);
      setReports((prev) => {
        const next = prev.filter((r) => r.id !== id);
        setSelectedId((cur) => (cur === id ? (next[0]?.id ?? null) : cur));
        return next;
      });
    } catch { toast.error("Couldn't delete that report."); }
  };

  const annotateSelected = async (r) => {
    try {
      await onBeforeRun?.();
      const { data } = await api.post(`/scripts/${scriptId}/coverage/${r.id}/annotations`);
      setReports((prev) => prev.map((x) => (x.id === data.report.id ? data.report : x)));
      startPolling(r.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't mark up the script.");
    }
  };

  const handleToggleAnnotations = () => {
    const next = !annotationsOn;
    onToggleAnnotations?.(next);
    if (!next) return;
    if (!selected) {
      toast("Notes will be added when you run coverage.");
      return;
    }
    if (selected.annotations_status === "ready") { onAnnotations?.(selected.annotations || [], selected.id); return; }
    if (selected.annotations_status === "generating" || selected.status === "generating") { startPolling(selected.id); return; }
    if (selected.status === "ready") annotateSelected(selected);
  };

  // Keep the editor's margin notes in sync when switching between past reads.
  useEffect(() => {
    if (annotationsOn && selected?.annotations_status === "ready") onAnnotations?.(selected.annotations || [], selected.id);
  }, [selectedId, annotationsOn]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open && !embedded) return null;

  const isGenerating = generating || selected?.status === "generating";

  const annotateToggle = (
    <button
      type="button"
      onClick={handleToggleAnnotations}
      role="switch"
      aria-checked={annotationsOn}
      className="flex items-center gap-1.5 group"
      data-testid="coverage-annotations-toggle"
      title="Reader's margin notes, shown beside your script lines in the editor"
    >
      <span className={`text-[11px] text-[var(--ink-secondary)] group-hover:text-[var(--ink-primary)] ${embedded ? "" : "hidden sm:inline"}`}>Annotate script</span>
      <span className={`relative inline-block w-8 h-[18px] rounded-full transition-colors ${annotationsOn ? "bg-[var(--brand)]" : "bg-[var(--line)]"}`}>
        <span className={`absolute top-[2px] left-[2px] w-[14px] h-[14px] rounded-full bg-white shadow transition-transform ${annotationsOn ? "translate-x-[14px]" : ""}`} />
      </span>
    </button>
  );

  const runBtn = (
    <button
      onClick={runCoverage}
      disabled={isGenerating}
      className="btn-brand text-sm inline-flex items-center gap-1.5 px-3 py-1.5 disabled:opacity-60"
      data-testid={reports.length ? "coverage-run-new-btn" : "coverage-generate-btn"}
    >
      {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
      {reports.length ? "New coverage" : "Run coverage"}
    </button>
  );

  const bodyContent = (
    <>
      {loading && !selected && (
        <div className="h-full flex items-center justify-center text-[var(--ink-muted)] text-sm" data-testid="coverage-loading">
          <Loader2 size={16} className="animate-spin mr-2" /> Loading…
        </div>
      )}

      {!loading && !selected && (
        <EmptyState onRun={runCoverage} generating={isGenerating} />
      )}

      {selected?.status === "generating" && (
        <div className="h-full min-h-[260px] flex flex-col items-center justify-center text-center" data-testid="coverage-status">
          <Loader2 size={28} className="animate-spin text-[var(--brand)]" />
          <p className="text-base text-[var(--ink-primary)] mt-4 font-display">Reading your screenplay…</p>
          <p className="text-sm text-[var(--ink-muted)] mt-1 max-w-xs">A full read usually takes under a minute. You can keep writing — we'll have it ready when you come back.</p>
        </div>
      )}

      {selected?.status === "error" && (
        <div className="h-full min-h-[260px] flex flex-col items-center justify-center text-center" data-testid="coverage-error">
          <AlertTriangle size={26} className="text-[var(--err)]" />
          <p className="text-base text-[var(--ink-primary)] mt-3">That read didn't finish</p>
          <p className="text-sm text-[var(--ink-muted)] mt-1">{selected.error || "Please try again."}</p>
          <button onClick={runCoverage} className="btn-brand text-sm mt-4 px-4 py-2">Try again</button>
        </div>
      )}

      {selected?.status === "ready" && (
        <>
          <GenreRow report={selected} scriptId={scriptId} declaredGenre={scriptGenre} />
          <Report report={selected} narrow={embedded} onCoachFocus={onCoachFocus} />
        </>
      )}
    </>
  );

  if (embedded) {
    return (
      <div className="flex flex-col h-full min-h-0" data-testid="coverage-panel">
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[var(--line)] shrink-0">
          {annotateToggle}
          {runBtn}
        </div>
        {reports.length > 0 && (
          <div className="flex gap-1.5 px-3 py-2 overflow-x-auto border-b border-[var(--line)] shrink-0" data-testid="coverage-history">
            {reports.map((r) => (
              <button
                key={r.id}
                onClick={() => setSelectedId(r.id)}
                className={`group shrink-0 text-left px-2.5 py-1.5 rounded-md border transition-colors ${selectedId === r.id ? "border-[var(--brand)] bg-[var(--brand-soft)]" : "border-[var(--line)] hover:bg-[var(--warm-accent)]"}`}
                data-testid={`coverage-history-${r.id}`}
              >
                <span className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-[var(--ink-primary)]">
                    {r.status === "ready" ? `${r.overall_score}/10` : r.status === "generating" ? "Reading…" : "Failed"}
                  </span>
                  <Trash2
                    size={11}
                    className="opacity-40 group-hover:opacity-100 text-[var(--ink-muted)] hover:text-[var(--err)] transition-opacity"
                    onClick={(e) => { e.stopPropagation(); deleteReport(r.id); }}
                    data-testid={`coverage-delete-${r.id}`}
                  />
                </span>
                <span className="block text-[10px] text-[var(--ink-muted)] mt-0.5">{fmtDate(r.created_at)}{r.stale ? " · older draft" : ""}</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4" data-testid="coverage-body">
          {bodyContent}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6" data-testid="coverage-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-[var(--paper)] rounded-xl shadow-2xl border border-[var(--line)] flex flex-col overflow-hidden">
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--line)] shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <ScrollText size={18} className="text-[var(--brand)] shrink-0" />
            <div className="min-w-0">
              <h2 className="font-display text-lg leading-none text-[var(--ink-primary)]">Coverage</h2>
              <p className="text-[11px] text-[var(--ink-muted)] mt-1 truncate">A professional reader's report on your draft</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="mr-1">{annotateToggle}</span>
            {runBtn}
            <button onClick={onClose} className="p-1.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]" data-testid="coverage-close" title="Close">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* history rail */}
          {reports.length > 0 && (
            <div className="w-40 shrink-0 border-r border-[var(--line)] overflow-y-auto py-2 hidden sm:block" data-testid="coverage-history">
              {reports.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setSelectedId(r.id)}
                  className={`w-full text-left px-3 py-2 group hover:bg-[var(--warm-accent)] ${selectedId === r.id ? "bg-[var(--brand-soft)]" : ""}`}
                  data-testid={`coverage-history-${r.id}`}
                >
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-xs font-semibold text-[var(--ink-primary)]">
                      {r.status === "ready" ? `${r.overall_score}/10` : r.status === "generating" ? "Reading…" : "Failed"}
                    </span>
                    <Trash2
                      size={12}
                      className="opacity-40 group-hover:opacity-100 text-[var(--ink-muted)] hover:text-[var(--err)] transition-opacity"
                      onClick={(e) => { e.stopPropagation(); deleteReport(r.id); }}
                      data-testid={`coverage-delete-${r.id}`}
                    />
                  </div>
                  <div className="text-[10px] text-[var(--ink-muted)] mt-0.5">{fmtDate(r.created_at)}</div>
                  {r.stale && <div className="text-[9px] uppercase tracking-wide text-[var(--warn)] mt-0.5">Older draft</div>}
                </button>
              ))}
            </div>
          )}

          {/* body */}
          <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5" data-testid="coverage-body">
            {bodyContent}
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onRun, generating }) {
  return (
    <div className="h-full min-h-[280px] flex flex-col items-center justify-center text-center max-w-md mx-auto">
      <div className="w-12 h-12 rounded-full bg-[var(--brand-soft)] flex items-center justify-center">
        <FileText size={22} className="text-[var(--brand)]" />
      </div>
      <p className="font-display text-xl text-[var(--ink-primary)] mt-4">Get coverage on your draft</p>
      <p className="text-sm text-[var(--ink-secondary)] mt-2 leading-relaxed">
        A professional-style read of your screenplay: logline, synopsis, scores across the six
        categories producers weigh, your script's strengths and what to fix — plus a verdict.
      </p>
      <button onClick={onRun} disabled={generating} className="btn-brand text-sm mt-5 px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60" data-testid="coverage-empty-generate-btn">
        {generating ? <Loader2 size={15} className="animate-spin" /> : <ScrollText size={15} />} Run coverage
      </button>
    </div>
  );
}

function Report({ report, narrow = false, onCoachFocus }) {
  const v = VERDICT_STYLE[report.verdict] || VERDICT_STYLE.Consider;
  const coachOn = (s) => {
    if (!onCoachFocus) return;
    const label = s.label || s.key;
    const path = (s.path || "").trim();
    const msg = `I want to work on ${label} — the coverage reader scored it ${s.score}/10.` +
      (path ? ` Their path to the next band: "${path}"` : "") + " Where do we start?";
    onCoachFocus(msg);
  };
  return (
    <div className="space-y-6">
      {report.stale ? (
        <div className="flex items-center gap-2 text-[11px] text-[var(--warn)] bg-[var(--warn)]/10 rounded px-3 py-1.5" data-testid="coverage-stale-chip">
          <Clock size={12} /> This read was made from an earlier draft — your script has changed since. Run a new coverage for a read of the current text.
        </div>
      ) : (
        <div className="flex items-center gap-2 text-[11px] text-[var(--ok)] bg-[var(--ok)]/10 rounded px-3 py-1.5" data-testid="coverage-fresh-chip">
          <Check size={12} /> Read from your current draft — the script hasn't changed since this read.
        </div>
      )}

      {/* verdict + overall */}
      <div className="flex items-center gap-5">
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] mb-1">Overall</div>
          <div className="text-4xl font-display leading-none" style={{ color: scoreColor(report.overall_score) }} data-testid="coverage-overall">
            {report.overall_score}<span className="text-lg text-[var(--ink-muted)]">/10</span>
          </div>
        </div>
        <div className="flex-1">
          <span
            className="inline-block text-white text-sm font-semibold px-3 py-1 rounded-full"
            style={{ backgroundColor: v.bg }}
            data-testid="coverage-verdict"
          >
            {v.label}
          </span>
          {report.verdict_reasoning && (
            <div className="text-sm text-[var(--ink-secondary)] mt-2 leading-relaxed" data-testid="coverage-verdict-reasoning"><MarkdownLite text={report.verdict_reasoning} /></div>
          )}
        </div>
      </div>

      {report.since_last && <SinceLast data={report.since_last} />}

      {/* logline */}
      {report.logline && (
        <div>
          <SectionLabel>Logline</SectionLabel>
          <div className="text-base text-[var(--ink-primary)] italic leading-relaxed" data-testid="coverage-logline"><MarkdownLite text={report.logline} /></div>
        </div>
      )}

      {/* synopsis */}
      {report.synopsis && (
        <div>
          <SectionLabel>Synopsis</SectionLabel>
          <div className="text-sm text-[var(--ink-secondary)] leading-relaxed" data-testid="coverage-synopsis"><MarkdownLite text={report.synopsis} /></div>
        </div>
      )}

      {/* scores */}
      <div>
        <SectionLabel>Scores</SectionLabel>
        <div className="space-y-4">
          {(report.scores || []).map((s) => (
            <div key={s.key} data-testid={`coverage-score-${s.key}`}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-semibold text-[var(--ink-primary)]">{s.label}</span>
                <span className="text-sm font-mono-script" style={{ color: scoreColor(s.score) }}>{s.score}</span>
              </div>
              <div className="h-1.5 rounded-full bg-[var(--warm-accent)] mt-1.5 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(s.score / 10) * 100}%`, backgroundColor: scoreColor(s.score) }} />
              </div>
              {s.comment && <div className="text-[13px] text-[var(--ink-secondary)] mt-1.5 leading-relaxed"><MarkdownLite text={s.comment} /></div>}
              {s.path && (
                <div className="mt-2 rounded-md bg-[var(--brand-soft)]/60 border border-[var(--brand)]/20 px-2.5 py-2" data-testid={`coverage-path-${s.key}`}>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--brand)] mb-1">Path to the next band</div>
                  <div className="text-[13px] text-[var(--ink-secondary)] leading-relaxed"><MarkdownLite text={s.path} /></div>
                  {onCoachFocus && (
                    <button
                      onClick={() => coachOn(s)}
                      className="mt-1.5 text-[11px] font-semibold text-[var(--brand)] hover:underline inline-flex items-center gap-1"
                      data-testid={`coverage-coach-btn-${s.key}`}
                      title="Open the Coach focused on this dimension"
                    >
                      <Compass size={11} /> Work on this with the Coach
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* strengths / weaknesses */}
      <div className={`grid gap-5 ${narrow ? "" : "sm:grid-cols-2"}`}>
        <div data-testid="coverage-strengths">
          <SectionLabel>Strengths</SectionLabel>
          <ul className="space-y-1.5">
            {(report.strengths || []).map((t, i) => (
              <li key={i} className="flex gap-2 text-[13px] text-[var(--ink-secondary)] leading-relaxed">
                <Check size={14} className="text-[var(--ok)] mt-0.5 shrink-0" /> <MarkdownLite text={t} className="flex-1 min-w-0" />
              </li>
            ))}
          </ul>
        </div>
        <div data-testid="coverage-weaknesses">
          <SectionLabel>What to develop</SectionLabel>
          <ul className="space-y-1.5">
            {(report.weaknesses || []).map((t, i) => (
              <li key={i} className="flex gap-2 text-[13px] text-[var(--ink-secondary)] leading-relaxed">
                <AlertTriangle size={13} className="text-[var(--warn)] mt-0.5 shrink-0" /> <MarkdownLite text={t} className="flex-1 min-w-0" />
              </li>
            ))}
          </ul>
        </div>
      </div>

      {report.annotations_status === "generating" && (
        <div className="flex items-center gap-1.5 text-[11px] text-[var(--ink-muted)]" data-testid="coverage-annotations-generating">
          <Loader2 size={11} className="animate-spin" /> Marking up your script with margin notes…
        </div>
      )}
      {report.annotations_status === "ready" && (report.annotations || []).length > 0 && (
        <div className="flex items-center gap-1.5 text-[11px] text-[var(--ink-muted)]" data-testid="coverage-annotations-info">
          <Highlighter size={11} /> {report.annotations.length} margin notes on this read — turn on "Annotate script" to see them beside your pages.
        </div>
      )}
      <p className="text-[11px] text-[var(--ink-muted)] pt-2 border-t border-[var(--line)]" data-testid="coverage-read-meta">
        {report.word_count ? `${report.word_count.toLocaleString()} words · ` : ""}{report.page_estimate} pp · read {fmtDate(report.created_at)}
        {report.prev_report_id ? " · compared with your previous read" : ""}
      </p>
    </div>
  );
}

function SectionLabel({ children }) {
  return <div className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] mb-2">{children}</div>;
}

const GENRES = [
  "Action", "Adventure", "Biopic", "Comedy", "Crime", "Dark Comedy", "Drama", "Family",
  "Fantasy", "Horror", "Musical", "Mystery", "Psychological Thriller", "Romance",
  "Romantic Comedy", "Sci-Fi", "Supernatural Drama", "Thriller", "War", "Western",
];

function GenreRow({ report, scriptId, declaredGenre }) {
  const [editing, setEditing] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [custom, setCustom] = useState("");
  const [savedGenre, setSavedGenre] = useState(declaredGenre || "");

  const save = async (v) => {
    const g = (v || "").trim().slice(0, 60);
    if (!g) return;
    try {
      await api.put(`/scripts/${scriptId}`, { genre: g });
      setSavedGenre(g);
      setEditing(false);
      setCustomOpen(false);
      setCustom("");
      toast.success(`Genre set to "${g}" — the reader will judge your next read against it.`);
    } catch {
      toast.error("Couldn't save the genre.");
    }
  };

  const shown = report.genre || "not stated";
  const source = report.genre_source === "writer" ? "set by you" : "reader's read";

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 mb-5 text-xs" data-testid="coverage-genre-row">
      <span className="text-[var(--ink-muted)] uppercase tracking-widest text-[10px]">Read as</span>
      <span className="font-semibold text-[var(--ink-primary)]" data-testid="coverage-genre-value">{shown}</span>
      <span className="text-[var(--ink-muted)]">({source})</span>
      {savedGenre && savedGenre !== report.genre && (
        <span className="text-[var(--brand)]" data-testid="coverage-genre-pending">→ next read: {savedGenre}</span>
      )}
      {!editing ? (
        <button onClick={() => setEditing(true)} className="text-[var(--brand)] hover:underline" data-testid="coverage-genre-correct-btn">
          Correct genre
        </button>
      ) : (
        <span className="flex items-center gap-1.5">
          <select
            defaultValue=""
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__custom__") { setCustomOpen(true); return; }
              setCustomOpen(false);
              if (v) save(v);
            }}
            className="text-xs bg-white border border-[var(--line)] rounded px-1.5 py-1 outline-none focus:border-[var(--brand)]"
            data-testid="coverage-genre-select"
          >
            <option value="" disabled>Choose genre…</option>
            {GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
            <option value="__custom__">Custom…</option>
          </select>
          {customOpen && (
            <>
              <input
                autoFocus
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") save(custom); }}
                placeholder="Your genre"
                className="text-xs bg-white border border-[var(--line)] rounded px-1.5 py-1 outline-none focus:border-[var(--brand)] w-32"
                data-testid="coverage-genre-custom-input"
              />
              <button onClick={() => save(custom)} className="text-[var(--brand)] hover:underline" data-testid="coverage-genre-custom-save">Save</button>
            </>
          )}
          <button onClick={() => { setEditing(false); setCustomOpen(false); }} className="text-[var(--ink-muted)] hover:underline" data-testid="coverage-genre-cancel">Cancel</button>
        </span>
      )}
    </div>
  );
}

function SinceLastRow({ items, icon: Icon, color, label, testId }) {
  if (!items?.length) return null;
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] mb-1">{label}</div>
      <ul className="space-y-1">
        {items.map((t, i) => (
          <li key={i} className="flex gap-2 text-[13px] text-[var(--ink-secondary)] leading-relaxed">
            <Icon size={13} className="mt-0.5 shrink-0" style={{ color }} /> <MarkdownLite text={t} className="flex-1 min-w-0" />
          </li>
        ))}
      </ul>
    </div>
  );
}

function SinceLast({ data }) {
  return (
    <div className="rounded-lg border border-[var(--line)] bg-[var(--warm-accent)]/40 p-4 space-y-3" data-testid="coverage-since-last">
      <div className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)]">Since your last draft</div>
      <SinceLastRow items={data.improved} icon={Check} color="var(--ok)" label="Improved" testId="since-last-improved" />
      <SinceLastRow items={data.regressed} icon={AlertTriangle} color="var(--err)" label="Regressed" testId="since-last-regressed" />
      <SinceLastRow items={data.unaddressed} icon={Clock} color="var(--warn)" label="Still unaddressed" testId="since-last-unaddressed" />
      {data.note && (
        <div className="text-[13px] text-[var(--ink-secondary)] italic leading-relaxed" data-testid="since-last-note">
          <MarkdownLite text={data.note} />
        </div>
      )}
    </div>
  );
}
